/**
 * توابع API، یکی به‌ازای هر روت. نازک و بدون منطق.
 *
 * ساخت مسیر متمرکز است (`paths`) تا رشته‌ی مسیر در چند فایل تکرار نشود؛ تکرار
 * مسیر یعنی روزی که backend مسیری را عوض کند، یکی از تکرارها جا بماند.
 */

import { slugFromPath } from '@/lib/base-path'

import {
  del,
  download,
  get,
  getCustomerKey,
  patch,
  post,
  put,
  setCustomerKey,
  upload,
} from './client'

import type {
  ArchiveDetail,
  ArchiveList,
  AssistantDefinition,
  AssistantInput,
  BulkCommitResult,
  BulkPreview,
  ConsultationState,
  JudgmentInput,
  JudgmentState,
  DecisionProfile,
  DossierDetail,
  DossierForm,
  DossierSummary,
  ItemStatus,
  Job,
  LessonStatus,
  Meta,
  LogicKind,
  Organization,
  OrganizationInput,
  PeopleBulkCommitResult,
  PeopleBulkPreview,
  PeopleList,
  Person,
  PersonInput,
  PrincipleConfidence,
  PrincipleReviewList,
  PrincipleReviewResult,
  ProcessingApplyResult,
  ProcessingState,
  PublicationPlan,
  RelationKind,
  ReviewDecision,
  LiveList,
  LiveState,
  RetrievalSettings,
  SecretStatus,
  SessionResponse,
  SimpleMessage,
  SourceDocument,
  UserFeedback,
} from '../types'

/** scope یک دستیار. در حالت مشتری هر دو خالی‌اند و سرور نادیده می‌گیردشان. */
export interface Scope {
  organizationId: string
  assistantId: string
}

const paths = {
  meta: '/api/meta',
  health: '/api/health',
  session: '/api/auth/session',
  consultations: '/api/consultations',
  consultation: (id: string) => `/api/consultations/${encodeURIComponent(id)}`,
  judgments: '/api/judgments',
  judgment: (id: string) => `/api/judgments/${encodeURIComponent(id)}`,
  archive: '/api/archive',
  archiveEntry: (id: string) => `/api/archive/${encodeURIComponent(id)}`,
  job: (id: string) => `/api/jobs/${encodeURIComponent(id)}`,
  organizations: '/api/organizations',
  organization: (org: string) => `/api/organizations/${encodeURIComponent(org)}`,
  organizationDocuments: (org: string) => `${paths.organization(org)}/documents`,
  people: (org: string) => `${paths.organization(org)}/people`,
  person: (org: string, personId: string) =>
    `${paths.people(org)}/${encodeURIComponent(personId)}`,
  assistants: (org: string) => `${paths.organization(org)}/assistants`,
  assistant: ({ organizationId, assistantId }: Scope) =>
    `${paths.assistants(organizationId)}/${encodeURIComponent(assistantId)}`,
  live: '/api/live',
  settingsProfile: '/api/settings/profile',
  settingsRetrieval: '/api/settings/retrieval',
  settingsSecrets: '/api/settings/secrets',
} as const

const scoped = (scope: Scope, suffix: string): string => `${paths.assistant(scope)}${suffix}`

/** پارامترها را به مسیر می‌چسباند. برای `POST` که هم body دارد و هم query. */
const withQuery = (url: string, params?: Record<string, string>): string => {
  if (params === undefined) {
    return url
  }
  const search = new URLSearchParams(params).toString()
  return search === '' ? url : `${url}?${search}`
}

/**
 * پارامتر scope برای روت‌های مشاوره و آرشیو.
 *
 * در حالت مشتری خالی می‌ماند و فرستاده نمی‌شود؛ سرور هم اگر بیاید نادیده
 * می‌گیرد، چون نسخه‌ی مشتری تک‌دستیار است.
 */
const scopeParams = (
  scope?: Scope,
): Record<string, string> | undefined =>
  scope === undefined || scope.organizationId === '' || scope.assistantId === ''
    ? undefined
    : { organization_id: scope.organizationId, assistant_id: scope.assistantId }

// ===================================================================
// meta و احراز هویت
// ===================================================================

/**
 * پیکربندی بوت.
 *
 * مسیر عمومی با هدر می‌رود (interceptor در `client.ts`)، پس اینجا پارامتری لازم
 * نیست. سرور با همان هدر پروفایل و برچسب‌های دستیار منتشرشده‌ی همان نشانی را
 * برمی‌گرداند؛ روی دامنه‌ی پنل نادیده گرفته می‌شود.
 */
export const fetchMeta = (): Promise<Meta> => get<Meta>(paths.meta)

/**
 * رمز را به توکن نشست تبدیل می‌کند و هویت پایدار مشتری را حفظ می‌کند.
 *
 * کلید هویت اگر از قبل باشد فرستاده می‌شود تا سرور همان شناسه را بازتولید کند؛
 * بدون آن، هر ورود یک مالک تازه می‌ساخت و آرشیو تصمیم‌های قبلی کاربر از دید او
 * ناپدید می‌شد. پاسخ سرور همان کلید یا کلید تازه را برمی‌گرداند و همان‌جا ذخیره
 * می‌شود.
 *
 * روی دامنه‌ی پنل `slug` خالی است و سرور هم هویت مشتری نمی‌دهد، پس این مسیر
 * برای اپراتور بی‌اثر است.
 */
export const createSession = async (accessCode: string): Promise<SessionResponse> => {
  const slug = slugFromPath()
  const session = await post<SessionResponse>(paths.session, {
    access_code: accessCode,
    customer_key: getCustomerKey(slug),
  })
  if (slug !== '' && session.customer_key !== '') {
    setCustomerKey(slug, session.customer_key)
  }
  return session
}

export const endSession = (): Promise<SimpleMessage> => del<SimpleMessage>(paths.session)

// ===================================================================
// مشاوره
// ===================================================================

export const startConsultation = (
  caseText: string,
  scope?: Scope,
): Promise<ConsultationState> =>
  post<ConsultationState>(withQuery(paths.consultations, scopeParams(scope)), {
    case_text: caseText,
  })

export const readConsultation = (id: string): Promise<ConsultationState> =>
  get<ConsultationState>(paths.consultation(id))

export const submitAnswers = (
  id: string,
  answers: (string | null)[],
): Promise<ConsultationState> =>
  post<ConsultationState>(`${paths.consultation(id)}/answers`, { answers })

export const refineConsultation = (
  id: string,
  payload: { revised_text: string; note: string; answers: (string | null)[] },
): Promise<ConsultationState> =>
  post<ConsultationState>(`${paths.consultation(id)}/refine`, payload)

/**
 * یک مشاوره‌ی بایگانی‌شده را به حافظه‌ی سرویس برمی‌گرداند.
 *
 * لازم است چون حالت در حال اجرا با TTL منقضی می‌شود؛ بدون این، «ادامه‌دادن» یک
 * تصمیم قدیمی ۴۰۴ می‌گرفت. scope همراه می‌رود تا در پنل، مشاوره‌ی همان دستیار
 * انتخاب‌شده پیدا شود.
 */
export const resumeConsultation = (
  id: string,
  scope?: Scope,
): Promise<ConsultationState> =>
  post<ConsultationState>(
    withQuery(`${paths.consultation(id)}/resume`, scopeParams(scope)),
  )

export const submitFeedback = (
  id: string,
  feedback: UserFeedback,
  correction: string,
): Promise<SimpleMessage> =>
  post<SimpleMessage>(`${paths.consultation(id)}/feedback`, { feedback, correction })

export const closeConsultation = (id: string): Promise<SimpleMessage> =>
  del<SimpleMessage>(paths.consultation(id))

// ===================================================================
// قضاوت مقایسه‌ای
// ===================================================================

/**
 * ⚠️ مسیر جدا از مشاوره است چون قرارداد پاسخ متفاوت است: قضاوت حکم
 * موافق/مخالف ندارد. یک مسیر مشترک با پرچم، مصرف‌کننده را مجبور می‌کرد شکل
 * پاسخ را در زمان اجرا حدس بزند.
 */
export const startJudgment = (
  payload: JudgmentInput,
  scope?: Scope,
): Promise<JudgmentState> =>
  post<JudgmentState>(withQuery(paths.judgments, scopeParams(scope)), payload)

export const readJudgment = (id: string): Promise<JudgmentState> =>
  get<JudgmentState>(paths.judgment(id))

export const submitJudgmentAnswers = (
  id: string,
  answers: (string | null)[],
): Promise<JudgmentState> =>
  post<JudgmentState>(`${paths.judgment(id)}/answers`, { answers })

export const closeJudgment = (id: string): Promise<void> => del<void>(paths.judgment(id))

export const downloadJudgment = (id: string, format: 'markdown' | 'json') =>
  download(`${paths.judgment(id)}/export`, { format })

/**
 * ردیف‌های برچسب‌دار پرونده‌ی نرمال‌شده.
 *
 * برچسب و ترتیب از backend می‌آید تا متن فارسی در یک جا بماند و فرانت‌اند
 * نگاشت فیلد به برچسب را دوباره پیاده نکند.
 */
export const fetchCaseRows = (id: string): Promise<Record<string, string>> =>
  get<Record<string, string>>(`${paths.consultation(id)}/case`)

export const downloadConsultation = (id: string, format: 'markdown' | 'json') =>
  download(`${paths.consultation(id)}/export`, { format })

// ===================================================================
// آرشیو
// ===================================================================

export const fetchArchive = (query: string, scope?: Scope): Promise<ArchiveList> =>
  get<ArchiveList>(paths.archive, { ...scopeParams(scope), query })

export const fetchArchiveEntry = (id: string, scope?: Scope): Promise<ArchiveDetail> =>
  get<ArchiveDetail>(paths.archiveEntry(id), scopeParams(scope))

// ===================================================================
// سازمان و دستیار
// ===================================================================

export const fetchOrganizations = (): Promise<Organization[]> =>
  get<Organization[]>(paths.organizations)

export const createOrganization = (input: OrganizationInput): Promise<Organization> =>
  post<Organization>(paths.organizations, input)

export const updateOrganization = (
  org: string,
  input: OrganizationInput,
): Promise<Organization> => put<Organization>(paths.organization(org), input)

/**
 * سازمان را با همه‌ی دستیارها و داده‌هایش حذف می‌کند.
 *
 * ⚠️ برگشت‌پذیر نیست و برخلاف سند و پرونده، بایگانی نمی‌شود. سازمانی که نسخه‌ی
 * زنده‌ی مشتری دارد از سمت سرور با ۴۰۹ رد می‌شود؛ ابتدا باید آن نسخه از دسترس
 * خارج شود.
 */
export const deleteOrganization = (org: string): Promise<SimpleMessage> =>
  del<SimpleMessage>(paths.organization(org))

/**
 * افراد سازمان و روابطشان.
 *
 * ⚠️ در scope **سازمان** است، نه دستیار: روابط خویشاوندی و سازمانی به طرز نگاه
 * هیچ دستیاری بستگی ندارند و همه‌ی دستیارهای یک سازمان باید یک تصویر ببینند.
 */
// ===================================================================
// پردازش دانش
// ===================================================================

export const fetchProcessing = (scope: Scope): Promise<ProcessingState> =>
  get<ProcessingState>(scoped(scope, '/processing'))

export const scanCorpus = (scope: Scope): Promise<ProcessingState> =>
  post<ProcessingState>(scoped(scope, '/processing/scan'), {})

/** گام پولی. `confirmed` صریح لازم است، مثل انتشار. */
export const draftLogic = (scope: Scope): Promise<ProcessingState> =>
  post<ProcessingState>(scoped(scope, '/processing/draft'), { confirmed: true })

/**
 * گام پولی استخراج هوشمند افراد و روابط.
 *
 * ⚠️ جدا از `draftLogic` است چون کاربر ممکن است فقط یکی را بخواهد: اگر افراد را
 * دستی وارد کرده، دلیلی برای پرداخت هزینه‌ی این گام ندارد.
 */
export const extractPeople = (scope: Scope): Promise<ProcessingState> =>
  post<ProcessingState>(scoped(scope, '/processing/extract-people'), {
    confirmed: true,
  })

export const decideRelation = (
  scope: Scope,
  candidateId: string,
  payload: { decision: ReviewDecision; kind?: RelationKind },
): Promise<ProcessingState> =>
  patch<ProcessingState>(
    scoped(scope, `/processing/relations/${encodeURIComponent(candidateId)}`),
    payload,
  )

export const decidePerson = (
  scope: Scope,
  candidateId: string,
  payload: {
    decision: ReviewDecision
    full_name?: string
    aliases?: string[]
    role?: string
    notes?: string
  },
): Promise<ProcessingState> =>
  patch<ProcessingState>(
    scoped(scope, `/processing/people/${encodeURIComponent(candidateId)}`),
    payload,
  )

export const decideLogic = (
  scope: Scope,
  candidateId: string,
  payload: { decision: ReviewDecision; statement?: string; kind?: LogicKind },
): Promise<ProcessingState> =>
  patch<ProcessingState>(
    scoped(scope, `/processing/logic/${encodeURIComponent(candidateId)}`),
    payload,
  )

export const addLogic = (
  scope: Scope,
  payload: { statement: string; kind: LogicKind },
): Promise<ProcessingState> =>
  post<ProcessingState>(scoped(scope, '/processing/logic'), payload)

/**
 * تصمیم یکسان روی چند کاندیدا با یک درخواست.
 *
 * ⚠️ شناسه‌ها تخت‌اند و backend خودش در افراد، روابط و بندهای منطق می‌گردد. پس
 * می‌توان انتخاب چند فهرست را یکجا فرستاد.
 */
export const bulkDecide = (
  scope: Scope,
  payload: { decision: ReviewDecision; candidate_ids: string[] },
): Promise<ProcessingState> =>
  post<ProcessingState>(scoped(scope, '/processing/decide-many'), payload)

export const answerProcessingQuestion = (
  scope: Scope,
  questionId: string,
  answer: string,
): Promise<ProcessingState> =>
  patch<ProcessingState>(
    scoped(scope, `/processing/questions/${encodeURIComponent(questionId)}`),
    { answer },
  )

export const applyProcessing = (scope: Scope): Promise<ProcessingApplyResult> =>
  post<ProcessingApplyResult>(scoped(scope, '/processing/apply'), {})

export const discardProcessing = (scope: Scope): Promise<SimpleMessage> =>
  del<SimpleMessage>(scoped(scope, '/processing'))

export const fetchPeople = (org: string): Promise<PeopleList> =>
  get<PeopleList>(paths.people(org))

export const createPerson = (org: string, input: PersonInput): Promise<Person> =>
  post<Person>(paths.people(org), input)

export const updatePerson = (
  org: string,
  personId: string,
  input: PersonInput,
): Promise<Person> => put<Person>(paths.person(org, personId), input)

export const deletePerson = (org: string, personId: string): Promise<SimpleMessage> =>
  del<SimpleMessage>(paths.person(org, personId))

/**
 * قالب xlsx بارگذاری گروهی افراد.
 *
 * جدا از قالب پرونده‌هاست چون افراد در scope سازمان‌اند و ستون‌هایشان هم فرق
 * دارد؛ یک قالب مشترک، هر دو را گیج‌کننده می‌کرد.
 */
export const downloadPeopleTemplate = (org: string) =>
  download(`${paths.people(org)}/template`)

export const previewBulkPeople = (
  org: string,
  file: File,
): Promise<PeopleBulkPreview> =>
  upload<PeopleBulkPreview>(`${paths.people(org)}/bulk`, file)

/**
 * افراد انتخاب‌شده را می‌نویسد.
 *
 * ⚠️ انتخاب با **نام** می‌رود، چون فرد تازه هنوز شناسه ندارد. فهرست خالی در
 * سرور معنای «همه» دارد.
 */
export const commitBulkPeople = (
  org: string,
  uploadToken: string,
  names: string[],
): Promise<PeopleBulkCommitResult> =>
  post<PeopleBulkCommitResult>(`${paths.people(org)}/bulk/commit`, {
    upload_token: uploadToken,
    names,
  })

export const fetchOrganizationDocuments = (org: string): Promise<SourceDocument[]> =>
  get<SourceDocument[]>(paths.organizationDocuments(org))

export const uploadOrganizationDocument = (
  org: string,
  file: File,
  title: string,
): Promise<SourceDocument> =>
  upload<SourceDocument>(paths.organizationDocuments(org), file, { title })

export const fetchAssistants = (org: string): Promise<AssistantDefinition[]> =>
  get<AssistantDefinition[]>(paths.assistants(org))

export const createAssistant = (
  org: string,
  input: AssistantInput,
): Promise<AssistantDefinition> => post<AssistantDefinition>(paths.assistants(org), input)

export const updateAssistant = (
  scope: Scope,
  input: AssistantInput,
): Promise<AssistantDefinition> => put<AssistantDefinition>(paths.assistant(scope), input)

export const fetchAssistantProfile = (scope: Scope): Promise<DecisionProfile> =>
  get<DecisionProfile>(scoped(scope, '/profile'))

// ===================================================================
// پرونده‌ی تصمیم
// ===================================================================

export const fetchDossiers = (
  scope: Scope,
  options: { query?: string; status?: ItemStatus } = {},
): Promise<DossierSummary[]> =>
  get<DossierSummary[]>(scoped(scope, '/dossiers'), {
    query: options.query ?? '',
    ...(options.status === undefined ? {} : { status: options.status }),
  })

export const fetchDossier = (scope: Scope, dossierId: string): Promise<DossierDetail> =>
  get<DossierDetail>(scoped(scope, `/dossiers/${encodeURIComponent(dossierId)}`))

export const createDossier = (scope: Scope, form: DossierForm): Promise<DossierDetail> =>
  post<DossierDetail>(scoped(scope, '/dossiers'), form)

export const updateDossier = (
  scope: Scope,
  dossierId: string,
  form: DossierForm,
): Promise<DossierDetail> =>
  put<DossierDetail>(scoped(scope, `/dossiers/${encodeURIComponent(dossierId)}`), form)

/**
 * تأیید یا رد گروهی اصول یک پرونده.
 *
 * ⚠️ `principle_count` نگهبان همزمانی است: اگر فهرست اصول از زمان خواندن عوض
 * شده باشد، backend ۴۰۹ می‌دهد تا نمایه‌ها به اصل دیگری اعمال نشوند.
 */
export const decidePrinciples = (
  scope: Scope,
  dossierId: string,
  payload: { principle_count: number; status: LessonStatus; indexes: number[] },
): Promise<DossierDetail> =>
  patch<DossierDetail>(
    scoped(scope, `/dossiers/${encodeURIComponent(dossierId)}/principles`),
    payload,
  )

/**
 * اصول همه‌ی پرونده‌های یک دستیار، برای مرور یک‌جا.
 *
 * ⚠️ فیلتر سمت سرور است چون همان قاعده‌ی نرمال‌سازی جست‌وجوی backend را
 * می‌خواهد؛ تکرارش در TypeScript دیر یا زود واگرا می‌شد.
 */
export const fetchPrinciples = (
  scope: Scope,
  filters: {
    query?: string
    status?: LessonStatus
    confidence?: PrincipleConfidence
    dossierStatus?: ItemStatus
  } = {},
): Promise<PrincipleReviewList> =>
  get<PrincipleReviewList>(scoped(scope, '/dossiers/principles'), {
    query: filters.query ?? '',
    ...(filters.status === undefined ? {} : { status: filters.status }),
    ...(filters.confidence === undefined ? {} : { confidence: filters.confidence }),
    ...(filters.dossierStatus === undefined
      ? {}
      : { dossier_status: filters.dossierStatus }),
  })

/**
 * تصمیم یکسان روی چند اصل از چند پرونده، با یک درخواست.
 *
 * `statement` هر قلم نگهبان همزمانی است: اگر متن روی دیسک عوض شده باشد، همان
 * قلم کنار گذاشته می‌شود و در `skipped` گزارش می‌آید — کل دسته رد نمی‌شود.
 */
export const decidePrinciplesInBulk = (
  scope: Scope,
  status: LessonStatus,
  items: { dossier_id: string; index: number; statement: string }[],
): Promise<PrincipleReviewResult> =>
  patch<PrincipleReviewResult>(scoped(scope, '/dossiers/principles'), {
    status,
    items,
  })

export const updateDossierStatus = (
  scope: Scope,
  dossierId: string,
  status: ItemStatus,
): Promise<DossierSummary> =>
  put<DossierSummary>(
    scoped(scope, `/dossiers/${encodeURIComponent(dossierId)}/status`),
    { status },
  )

/**
 * قالب xlsx بارگذاری گروهی.
 *
 * scope می‌گیرد هرچند قالب برای همه‌ی دستیارها یکی است: روت زیر مسیر دستیار
 * نشسته و گارد `ensure_studio` و اعتبارسنجی شناسه از همان‌جا می‌آید.
 */
export const downloadDossierTemplate = (scope: Scope) =>
  download(scoped(scope, '/dossiers/template'))

export const previewBulkDossiers = (
  scope: Scope,
  file: File,
  defaultStatus: ItemStatus,
): Promise<BulkPreview> =>
  upload<BulkPreview>(scoped(scope, '/dossiers/bulk'), file, {
    default_status: defaultStatus,
  })

export const commitBulkDossiers = (
  scope: Scope,
  uploadToken: string,
  dossierIds: string[],
): Promise<BulkCommitResult> =>
  post<BulkCommitResult>(scoped(scope, '/dossiers/bulk/commit'), {
    upload_token: uploadToken,
    dossier_ids: dossierIds,
  })

export const fetchDossierDocuments = (
  scope: Scope,
  dossierId: string,
): Promise<SourceDocument[]> =>
  get<SourceDocument[]>(scoped(scope, `/dossiers/${encodeURIComponent(dossierId)}/documents`))

export const uploadDossierDocument = (
  scope: Scope,
  dossierId: string,
  file: File,
  title: string,
): Promise<SourceDocument> =>
  upload<SourceDocument>(
    scoped(scope, `/dossiers/${encodeURIComponent(dossierId)}/documents`),
    file,
    { title },
  )

export const archiveDossierDocument = (
  scope: Scope,
  dossierId: string,
  documentId: string,
): Promise<SimpleMessage> =>
  del<SimpleMessage>(
    scoped(
      scope,
      `/dossiers/${encodeURIComponent(dossierId)}/documents/${encodeURIComponent(documentId)}`,
    ),
  )

// ===================================================================
// انتشار
// ===================================================================

export const fetchPublicationPlan = (scope: Scope): Promise<PublicationPlan> =>
  get<PublicationPlan>(scoped(scope, '/publication/plan'))

export const startPublication = (scope: Scope, note: string): Promise<Job> =>
  post<Job>(scoped(scope, '/publication'), { note, confirmed: true })

export const fetchActivePublication = (scope: Scope): Promise<Job | null> =>
  get<Job | null>(scoped(scope, '/publication/active-job'))

export const fetchRevisions = (scope: Scope) => get(scoped(scope, '/revisions'))

export const fetchJob = (jobId: string): Promise<Job> => get<Job>(paths.job(jobId))

// ===================================================================
// نسخه‌ی زنده‌ی مشتری
// ===================================================================

export const fetchLiveState = (scope: Scope): Promise<LiveState> =>
  get<LiveState>(scoped(scope, '/live'))

/** نسخه‌ی فعال دانش را روی مسیر عمومی زنده می‌کند (یا به‌روز می‌کند). */
/**
 * نسخه‌ی فعال دانش را روی مسیر عمومی زنده می‌کند (یا به‌روز می‌کند).
 *
 * `access_code` رمز همین نسخه است. در نخستین انتشار الزامی است؛ در به‌روزرسانی
 * خالی گذاشتنش یعنی «رمز فعلی بماند»، تا دسترسی مشتری قطع نشود.
 */
export const publishLive = (
  scope: Scope,
  payload: { slug: string; note: string; access_code: string },
): Promise<LiveState> => put<LiveState>(scoped(scope, '/live'), payload)

export const unpublishLive = (scope: Scope): Promise<SimpleMessage> =>
  del<SimpleMessage>(scoped(scope, '/live'))

export const fetchLiveList = (): Promise<LiveList> => get<LiveList>(paths.live)

// ===================================================================
// تنظیمات
// ===================================================================

export const fetchProfile = (): Promise<DecisionProfile> =>
  get<DecisionProfile>(paths.settingsProfile)

export const saveProfile = (profile: DecisionProfile): Promise<DecisionProfile> =>
  put<DecisionProfile>(paths.settingsProfile, profile)

export const resetProfile = (): Promise<DecisionProfile> =>
  del<DecisionProfile>(paths.settingsProfile)

export const fetchRetrievalSettings = (): Promise<RetrievalSettings> =>
  get<RetrievalSettings>(paths.settingsRetrieval)

export const fetchSecretStatus = (): Promise<SecretStatus> =>
  get<SecretStatus>(paths.settingsSecrets)
